/* AZADEH storefront behaviour, additive to editorial-commerce.js.

   1. Mobile campaign hero - one panel per screen, dots, and the class the CSS
      uses to run each panel's arrival transition.
   2. Mobile header - hides going down, returns on the way up.
   3. Grid density buttons - make them do something on a phone.

   Everything here no-ops above 800px, so desktop behaviour is untouched.

   Note on requestAnimationFrame: it is used only to smooth work that is already
   correct without it. State is never updated inside a rAF callback, because a
   backgrounded or non-compositing tab stops firing them and the carousel would
   silently stop tracking which slide is showing.
*/
(function () {
  "use strict";

  var MOBILE = "(max-width: 800px)";

  function isMobile() {
    return window.matchMedia(MOBILE).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /* -- 1. Mobile campaign hero ------------------------------------------- */

  function initHero() {
    var rail = document.querySelector(".vertical-campaign__rail");
    if (!rail || rail.dataset.azHeroReady === "true") return;

    var panels = Array.prototype.slice.call(
      rail.querySelectorAll("[data-campaign-panel]")
    );
    if (panels.length < 2) return;
    rail.dataset.azHeroReady = "true";

    var dots = document.createElement("div");
    dots.className = "az-hero-dots";
    dots.setAttribute("role", "tablist");
    dots.setAttribute("aria-label", "Campaign slides");

    var current = -1;

    var buttons = panels.map(function (panel, i) {
      var b = document.createElement("button");
      b.type = "button";
      b.setAttribute("role", "tab");
      b.setAttribute(
        "aria-label",
        "Go to slide " + (i + 1) + " of " + panels.length
      );
      b.addEventListener("click", function () {
        goTo(i);
      });
      dots.appendChild(b);
      return b;
    });

    rail.insertAdjacentElement("afterend", dots);

    function setActive(index) {
      if (index < 0 || index === current) return;
      current = index;
      panels.forEach(function (p, i) {
        p.classList.toggle("az-in-view", i === index);
      });
      buttons.forEach(function (b, i) {
        b.setAttribute("aria-current", i === index ? "true" : "false");
      });
    }

    function goTo(index) {
      var left = index * rail.clientWidth;
      setActive(index);
      /* Explicit behavior in the options rather than relying on CSS
         scroll-behavior, which the document root sets to smooth and which
         cancels programmatic scrolls on a mandatory snap container. */
      if (prefersReducedMotion() || typeof rail.scrollTo !== "function") {
        rail.scrollLeft = left;
        return;
      }
      rail.scrollTo({ left: left, behavior: "smooth" });
      /* Safety net: if the smooth scroll is refused or interrupted, land the
         rail on the right slide anyway rather than leaving it mid-swipe. */
      window.setTimeout(function () {
        if (Math.abs(rail.scrollLeft - left) > 4) rail.scrollLeft = left;
      }, 500);
    }

    /* Which panel is showing, derived straight from scroll position. Done in
       the scroll handler itself - it is one division, and deferring it to a
       rAF meant it never ran when frames were not being produced. */
    rail.addEventListener(
      "scroll",
      function () {
        var w = rail.clientWidth;
        if (!w) return;
        var index = Math.round(rail.scrollLeft / w);
        setActive(Math.max(0, Math.min(panels.length - 1, index)));
      },
      { passive: true }
    );

    setActive(0);
  }

  /* -- 2. Mobile header -------------------------------------------------- */

  function initHeader() {
    var header = document.querySelector(".site-header");
    if (!header || header.dataset.azHeaderReady === "true") return;
    header.dataset.azHeaderReady = "true";

    /* The old markup opened the mobile nav with a <details>; the drawer that
       replaced it is driven by a class on <body>. Both are checked so the
       header cannot slide away while the nav is open either way. */
    var menu = header.querySelector(".site-header__mobile-menu");
    var last = window.pageYOffset;
    var THRESHOLD = 6; // ignore sub-pixel jitter and rubber-banding

    function navOpen() {
      return (
        document.body.classList.contains("drawer-open") ||
        Boolean(menu && menu.open)
      );
    }

    window.addEventListener(
      "scroll",
      function () {
        var y = window.pageYOffset;

        if (!isMobile() || navOpen()) {
          document.body.classList.remove("az-nav-hidden");
          last = y;
          return;
        }

        var delta = y - last;
        if (Math.abs(delta) <= THRESHOLD) return;

        // Only hide past the header's own height, so it never hides at the top.
        document.body.classList.toggle(
          "az-nav-hidden",
          delta > 0 && y > header.offsetHeight
        );
        last = y;
      },
      { passive: true }
    );

    if (menu) {
      menu.addEventListener("toggle", function () {
        if (menu.open) document.body.classList.remove("az-nav-hidden");
      });
      document.addEventListener("click", function (event) {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });
      document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && menu.open) menu.open = false;
      });
    }
  }

  /* -- 3. Grid density on mobile ----------------------------------------- */

  /* The 2 / 3 / 4 buttons set --collection-columns, but below 800px the grid
     is laid out from --collection-mobile-columns instead, so on a phone the
     buttons highlighted and nothing moved. Mirroring the value into the mobile
     variable makes them work at every width without touching the default,
     which still comes from the section setting on first paint. */
  function initDensity() {
    document.addEventListener("click", function (event) {
      var button = event.target.closest
        ? event.target.closest("[data-density-button]")
        : null;
      if (!button) return;
      var host = button.closest("editorial-collection");
      if (!host) return;
      host.style.setProperty(
        "--collection-mobile-columns",
        button.dataset.densityButton || "2"
      );
    });
  }

  function init() {
    initHero();
    initHeader();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", init);

  /* Delegated, so it is bound once and survives section re-renders. */
  initDensity();
})();